import { ItemGroupMode } from '@VelocitySquared/sunny16-commerce-dataobjects/enums'
import type {
  CartBundleStorefrontDto,
  CartItemStorefrontDto,
  CatalogBundleStorefrontDto,
  ItemGroupItemStorefrontDto,
  ItemGroupStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'
import {
  CartBundleUpdateStorefrontDto,
  CartItemUpdateStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

// Builds the payload that moves every selection (non-`isIncluded`, non-`SheetSelection` item group) in a cart bundle to
// its highest-priced option — the mechanism behind the paper-type upgrade on cart/configure (SUN-344). Generic over item
// groups because nothing in the storefront knows what paper is. Pure: no store access.
//
// Rationale and verified server behaviour:
// `docs/features/2026.09.10-paper-type-upgrade-timing/implementation-guides/2026.09.10-001-paper-type-upgrade-timing.md`

/** A cart bundle's current selection for one item group, as it exists in the cart right now. */
interface CurrentGroupSelection {
  cartItems: CartItemStorefrontDto[]
  itemGroupItemIds: string[]
}

function optionPrice(
  catalogBundle: CatalogBundleStorefrontDto,
  itemGroupItemId: string
): number | null {
  const detail = catalogBundle.pricing?.optionalItemPrices?.[itemGroupItemId]
  return detail ? detail.price : null
}

/**
 * The group's highest-priced option among those with a pricing row; ties break to the lowest `zOrder`, matching the add
 * path's default. Unpriced options are excluded rather than treated as $0 — Sunny16 rejects adding an option without pricing.
 */
function highestPricedOption(
  group: ItemGroupStorefrontDto,
  catalogBundle: CatalogBundleStorefrontDto
): ItemGroupItemStorefrontDto | null {
  const priced = (group.itemGroupItems ?? [])
    .map((igi) => ({ igi, price: optionPrice(catalogBundle, igi.itemGroupItemId) }))
    .filter((candidate): candidate is { igi: ItemGroupItemStorefrontDto; price: number } => {
      return candidate.price !== null
    })

  if (priced.length === 0) return null

  return priced.reduce((best, candidate) => {
    if (candidate.price !== best.price) return candidate.price > best.price ? candidate : best
    return (candidate.igi.zOrder || 0) < (best.igi.zOrder || 0) ? candidate : best
  }).igi
}

function currentSelectionForGroup(
  cartBundle: CartBundleStorefrontDto,
  group: ItemGroupStorefrontDto
): CurrentGroupSelection {
  const groupItemGroupItemIds = new Set(
    (group.itemGroupItems ?? []).map((igi) => igi.itemGroupItemId)
  )

  const cartItems = cartBundle.cartItems.filter(
    (ci) => !!ci.itemGroupItemId && groupItemGroupItemIds.has(ci.itemGroupItemId)
  )

  return { cartItems, itemGroupItemIds: cartItems.map((ci) => ci.itemGroupItemId) }
}

function asSelection(itemGroupItemId: string, itemId: string): CartItemUpdateStorefrontDto {
  const selection = new CartItemUpdateStorefrontDto()
  selection.itemGroupItemId = itemGroupItemId
  selection.itemId = itemId
  return selection
}

/** Distinct prices among the group's priced options. Fewer than two means there is nothing to upgrade to. */
function distinctPriceCount(
  group: ItemGroupStorefrontDto,
  catalogBundle: CatalogBundleStorefrontDto
): number {
  const prices = (group.itemGroupItems ?? [])
    .map((igi) => optionPrice(catalogBundle, igi.itemGroupItemId))
    .filter((price): price is number => price !== null)

  return new Set(prices).size
}

/**
 * What the group should end up selected on: the highest-priced option, or the current selection carried forward
 * verbatim. A group omitted from `selectedItems` has its cart items deleted server-side, so "leave alone" must be spelled
 * as "re-send what it has".
 *
 * Upgrade is by price, not identity: the selection moves only when a strictly more expensive option exists. The current
 * selection is carried forward when the group holds more than one selection, the current option has no pricing row
 * (removing it throws server-side), nothing in the group is priced, fewer than two prices exist, or the current option
 * already costs as much as the priciest. A group with no selection at all (an optional group the customer never chose
 * from) is left empty: there is nothing to upgrade, and filling it would add a charge from nowhere.
 */
function resolveGroupSelections(
  cartBundle: CartBundleStorefrontDto,
  group: ItemGroupStorefrontDto,
  catalogBundle: CatalogBundleStorefrontDto
): CartItemUpdateStorefrontDto[] {
  const current = currentSelectionForGroup(cartBundle, group)

  const carryCurrentForward = (): CartItemUpdateStorefrontDto[] =>
    current.cartItems.map((ci) => asSelection(ci.itemGroupItemId, ci.itemId))

  if (current.cartItems.length !== 1) return carryCurrentForward()

  const currentCartItem = current.cartItems[0]
  const currentPrice = optionPrice(catalogBundle, currentCartItem.itemGroupItemId)

  if (currentPrice === null) return carryCurrentForward()

  const target = highestPricedOption(group, catalogBundle)

  if (!target) return carryCurrentForward()

  if (distinctPriceCount(group, catalogBundle) < 2) return carryCurrentForward()

  // Compared as numbers so a same-price alternative is left alone.
  const targetPrice = optionPrice(catalogBundle, target.itemGroupItemId) ?? 0

  if (currentPrice >= targetPrice) return carryCurrentForward()

  return [asSelection(target.itemGroupItemId, target.itemId)]
}

/** The option the add path defaults to: lowest `zOrder` across all options (see `getAutoSelectedRequiredItems`). */
function addTimeDefaultOption(
  group: ItemGroupStorefrontDto
): ItemGroupItemStorefrontDto | undefined {
  return [...(group.itemGroupItems ?? [])].sort((a, b) => (a.zOrder || 0) - (b.zOrder || 0))[0]
}

function selectableGroups(catalogBundle: CatalogBundleStorefrontDto): ItemGroupStorefrontDto[] {
  return (catalogBundle.bundle?.itemGroups ?? []).filter(
    (ig) => !ig.isIncluded && ig.mode !== ItemGroupMode.SheetSelection
  )
}

/**
 * One `CartBundleUpdateStorefrontDto` per cart bundle with something to upgrade, for a single `updateCartConfiguration`
 * call. Empty means skip the request.
 *
 * Shape constraints, verified against `CartStorefrontService.UpdateCartConfiguration`: only bundles that change are
 * included (the endpoint touches exactly what it is given); an included bundle carries every selectable group, since
 * `selectedItems` is a full replace that deletes what is absent; `customizations` stays null, which the endpoint treats
 * as "leave alone"; `quantity` is copied because the endpoint applies it unconditionally and the DTO defaults to `0`.
 */
export function buildMaxPriceSelectionPayload(
  cartBundles: CartBundleStorefrontDto[],
  catalogBundles: CatalogBundleStorefrontDto[]
): CartBundleUpdateStorefrontDto[] {
  const payload: CartBundleUpdateStorefrontDto[] = []

  cartBundles.forEach((cartBundle) => {
    const catalogBundle = catalogBundles.find(
      (cb) => cb.catalogBundleId === cartBundle.catalogBundleId
    )

    // No catalog bundle means no `optionalItemPrices` to rank by; the page renders no selection UI for it either.
    if (!catalogBundle) return

    const groups = selectableGroups(catalogBundle)
    if (groups.length === 0) return

    const selectedItems: CartItemUpdateStorefrontDto[] = []
    let changesSomething = false

    groups.forEach((group) => {
      const resolved = resolveGroupSelections(cartBundle, group, catalogBundle)
      if (resolved.length === 0) return

      selectedItems.push(...resolved)

      // Set comparison rather than length, so a same-size swap still counts as a change.
      const current = new Set(currentSelectionForGroup(cartBundle, group).itemGroupItemIds)
      const movedGroup =
        resolved.length !== current.size ||
        resolved.some((selection) => !current.has(selection.itemGroupItemId))

      if (movedGroup) changesSomething = true
    })

    // Nothing changed — leave the bundle out, so an already-upgraded cart yields an empty payload.
    if (!changesSomething || selectedItems.length === 0) return

    const bundleUpdate = new CartBundleUpdateStorefrontDto()
    bundleUpdate.cartBundleId = cartBundle.cartBundleId
    bundleUpdate.quantity = cartBundle.quantity
    bundleUpdate.selectedItems = selectedItems

    payload.push(bundleUpdate)
  })

  return payload
}

/**
 * Requested selections missing from the returned cart, as `cartBundleId:itemGroupItemId` strings for reporting. Catches
 * a 200 that did not move the selections, which is otherwise silent.
 */
export function findUnappliedSelections(
  cartBundles: CartBundleStorefrontDto[],
  payload: CartBundleUpdateStorefrontDto[]
): string[] {
  const unapplied: string[] = []

  payload.forEach((bundleUpdate) => {
    const cartBundle = cartBundles.find((cb) => cb.cartBundleId === bundleUpdate.cartBundleId)

    if (!cartBundle) {
      unapplied.push(`${bundleUpdate.cartBundleId}:bundle-missing`)
      return
    }

    const present = new Set(cartBundle.cartItems.map((ci) => ci.itemGroupItemId))

    for (const selection of bundleUpdate.selectedItems ?? []) {
      if (!present.has(selection.itemGroupItemId)) {
        unapplied.push(`${bundleUpdate.cartBundleId}:${selection.itemGroupItemId}`)
      }
    }
  })

  return unapplied
}

/**
 * Selectable groups whose priciest option is also the add path's default (lowest `zOrder`), as
 * `cartBundleId:itemGroupId:nameInternal` strings for reporting. In that state add-to-cart charges the upgrade and the
 * cart/configure upgrade is a silent no-op, so the empty payload cannot be reported on and this check stands in for it.
 * Groups with fewer than two distinct prices have nothing to mis-order and are skipped.
 */
export function findMisorderedSelectionGroups(
  cartBundles: CartBundleStorefrontDto[],
  catalogBundles: CatalogBundleStorefrontDto[]
): string[] {
  const misordered: string[] = []

  cartBundles.forEach((cartBundle) => {
    const catalogBundle = catalogBundles.find(
      (cb) => cb.catalogBundleId === cartBundle.catalogBundleId
    )

    if (!catalogBundle) return

    selectableGroups(catalogBundle).forEach((group) => {
      if (distinctPriceCount(group, catalogBundle) < 2) return

      const priciest = highestPricedOption(group, catalogBundle)
      const addTimeDefault = addTimeDefaultOption(group)

      if (!priciest || !addTimeDefault) return

      if (priciest.itemGroupItemId === addTimeDefault.itemGroupItemId) {
        misordered.push(`${cartBundle.cartBundleId}:${group.itemGroupId}:${group.nameInternal}`)
      }
    })
  })

  return misordered
}

/**
 * The bundles the upgrade may touch, given what cart/configure renders (`cartBundlesRequiringConfiguration`). The page's
 * own getter is the gate, so exclusions are not re-derived here. Reward bundles (`sourcePromotionId`) are removed on top:
 * auto-upgrading one would add a charge to something advertised as free.
 */
export function upgradeableCartBundles(
  bundlesShownOnCartConfigure: CartBundleStorefrontDto[]
): CartBundleStorefrontDto[] {
  return bundlesShownOnCartConfigure.filter((cartBundle) => !cartBundle.sourcePromotionId)
}
